import React from "react";
import styled from "styled-components";
import AppIcon, { AppIconType } from "../AppIcon/AppIcon";
import AppText, { FormLabel } from "../AppText/AppText";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import { Flex, Icon, Tooltip, Button as UIButton } from "@chakra-ui/react";

interface TextAreaStyles {
	height?: string;
	containerMargin?: string;
	width?: string;
	disabled?: boolean;
}

interface ITextAreaProps extends TextAreaStyles {
	value?: string;
	onChange?: any;
	onChangeText?: (text: string) => void;
	name?: string;
	errors?: any;
	field?: string;
	placeholder?: string;
	icon?: AppIconType;
	label?: string;
	description?: string;
	/**
	 * Will shows asterisk next to label which indicates that field
	 * is mandatory. Will not add any validation logic!
	 */
	mandatory?: boolean;
	disabled?: boolean;
	aiGenerateButton?: {
		onClickAiGenerateButton?: () => void;
		aiGenerateButtonText?: string;
		disabledAiGenerateButton?: boolean;
		loadingAiGenerateButton?: boolean;
		tooltipText?: string;
		loadingText?: string;
	};
}

const TextArea = React.forwardRef<HTMLTextAreaElement, ITextAreaProps>(
	(
		{
			value,
			onChange,
			onChangeText,
			name,
			errors,
			icon,
			height,
			placeholder,
			containerMargin,
			width,
			label,
			description,
			mandatory,
			disabled,
			aiGenerateButton,
		},
		ref
	) => {
		const { t } = useTranslation();

		return (
			<TextAreaContainer width={width} containerMargin={containerMargin} height={height}>
				<Flex justifyContent={"space-between"} alignItems="center" marginBottom="0.5rem">
					<FormLabel>
						{label && t(label as any)}
						{mandatory ? " *" : ""}
					</FormLabel>
					{icon && <AppIcon icon={icon} />}
					{aiGenerateButton && (
						<Tooltip
							label={aiGenerateButton.tooltipText}
							isDisabled={!aiGenerateButton.disabledAiGenerateButton}
							placement="top"
						>
							<span>
								<UIButton
									onClick={aiGenerateButton.onClickAiGenerateButton}
									disabled={aiGenerateButton.disabledAiGenerateButton}
									isLoading={aiGenerateButton.loadingAiGenerateButton}
									loadingText={aiGenerateButton.loadingText}
									type="button"
									variant="ghost"
									color="blue.500"
								>
									<Icon
										color="blue.500"
										boxSize="1.5rem"
										cursor="pointer"
										style={{ marginRight: "0.4rem" }}
									>
										<AppIcon icon="ai" />
									</Icon>
									{t(aiGenerateButton.aiGenerateButtonText as any)}
								</UIButton>
							</span>
						</Tooltip>
					)}
				</Flex>
				<div>
					<textarea
						name={name}
						onChange={(e) => {
							if (onChangeText) {
								onChangeText(e.target.value);
							} else if (onChange) {
								onChange(e);
							}
						}}
						value={value}
						ref={ref}
						placeholder={placeholder && t(placeholder as any)}
						disabled={disabled}
						style={{ cursor: disabled ? "not-allowed" : "auto" }}
					/>
					{description && (
						<AppText margin="0.3rem 0 0 0.15rem" size="xs" color="grayText">
							{t(description as any)}
						</AppText>
					)}
					<AppText margin="0.3rem 0 0 0.15rem" size="xs" color="danger">
						{errors && t(errors as any)}
					</AppText>
				</div>
			</TextAreaContainer>
		);
	}
);

export default TextArea;

const TextAreaContainer = styled.div<TextAreaStyles>`
	textarea {
		width: ${(props) => (props.width ? props.width : "100%")};
		height: ${(props) => (props.height ? props.height : "75px")};
		background: ${(props) => props.theme.colors.COLOR_WHITE} 0% 0% no-repeat padding-box;
		border-radius: 2px;
		padding: 0.75rem;
		font-size: ${(props) => props.theme.fontSizes.FONT_SIZE_M};
		font-weight: ${(props) => props.theme.fontWeight.FONT_WEIGHT_REGULAR};
		resize: none;
		border: 1px solid ${(props) => props.theme.colors.INPUT_BORDER};

		&:focus {
			border-color: ${(props) => props.theme.colors.INPUT_BORDER_FOCUSED};
		}
	}
	margin: ${(props) => props.containerMargin};
`;
